import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../context/AuthContext';
import {
  featurePopupService,
  evaluateEligiblePopup,
  dismissPopup,
  snoozePopup,
  resetPopupDismissal,
} from '../../services/featurePopupService';
import { FeaturePopup } from '../../models/FeaturePopup';
import { useState, useEffect, useCallback } from 'react';
import { navigate } from '../../utils/navigation';

export const FEATURE_POPUPS_QUERY_KEY = ['featurePopups'];

export const useFeaturePopups = (options?: { enabled?: boolean }) => {
  return useQuery<FeaturePopup[]>({
    queryKey: FEATURE_POPUPS_QUERY_KEY,
    queryFn: () => featurePopupService.getAllPopups(),
    enabled: options?.enabled ?? true,
    staleTime: 5 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });
};

export const useFeaturePopup = (id?: string | null) => {
  const queryClient = useQueryClient();
  return useQuery<FeaturePopup | null>({
    queryKey: ['featurePopup', id],
    queryFn: () => (id ? featurePopupService.getPopupById(id) : null),
    initialData: () => {
      if (!id) return undefined;
      const cached = queryClient.getQueryData<FeaturePopup[]>(FEATURE_POPUPS_QUERY_KEY);
      return cached?.find((p) => p.id === id);
    },
    enabled: !!id,
  });
};

export const useSaveFeaturePopup = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (popup: Partial<FeaturePopup>) => {
      return await featurePopupService.savePopup(popup, user?.uid);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: FEATURE_POPUPS_QUERY_KEY });
      if (variables?.id) {
        queryClient.invalidateQueries({ queryKey: ['featurePopup', variables.id] });
      }
    },
  });
};

export const useDeleteFeaturePopup = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await featurePopupService.deletePopup(id);
      return id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: FEATURE_POPUPS_QUERY_KEY });
    },
  });
};

export const useToggleFeaturePopup = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, isEnabled }: { id: string; isEnabled: boolean }) => {
      await featurePopupService.toggleEnabled(id, isEnabled);
      return { id, isEnabled };
    },
    onMutate: async ({ id, isEnabled }) => {
      await queryClient.cancelQueries({ queryKey: FEATURE_POPUPS_QUERY_KEY });
      const previousPopups = queryClient.getQueryData<FeaturePopup[]>(FEATURE_POPUPS_QUERY_KEY);

      if (previousPopups) {
        queryClient.setQueryData<FeaturePopup[]>(
          FEATURE_POPUPS_QUERY_KEY,
          previousPopups.map((p) => (p.id === id ? { ...p, isEnabled } : p))
        );
      }
      return { previousPopups };
    },
    onError: (_err, _variables, context) => {
      if (context?.previousPopups) {
        queryClient.setQueryData(FEATURE_POPUPS_QUERY_KEY, context.previousPopups);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: FEATURE_POPUPS_QUERY_KEY });
    },
  });
};

/**
 * Hook for displaying the feature popup to users when they open the app or log in.
 */
export const useEligibleFeaturePopup = () => {
  const { user, profile, loading: authLoading } = useAuth();
  const { data: popups = [], isLoading: popupsLoading } = useFeaturePopups({
    enabled: !authLoading,
  });

  const [activePopup, setActivePopup] = useState<FeaturePopup | null>(null);

  // Evaluate on load or when auth/popups change
  useEffect(() => {
    if (authLoading || popupsLoading || popups.length === 0) {
      setActivePopup(null);
      return;
    }

    const eligible = evaluateEligiblePopup(popups, user, profile);
    setActivePopup(eligible);
  }, [authLoading, popupsLoading, popups, user, profile]);

  const handleDismiss = useCallback(() => {
    if (activePopup) {
      dismissPopup(activePopup.id, activePopup.version || 1);
      setActivePopup(null);
    }
  }, [activePopup]);

  const handleSnooze = useCallback((hours = 24) => {
    if (activePopup) {
      snoozePopup(activePopup.id, hours);
      setActivePopup(null);
    }
  }, [activePopup]);

  const handleAction = useCallback(() => {
    if (!activePopup) return;

    // Mark as permanently dismissed so it does not annoy user again
    dismissPopup(activePopup.id, activePopup.version || 1);
    const popup = activePopup;
    setActivePopup(null);

    // Handle navigation
    if (popup.actionType === 'event' && popup.eventId) {
      navigate(`/events/${popup.eventId}`);
    } else if (popup.actionType === 'url' && popup.actionUrl) {
      const url = popup.actionUrl.trim();
      if (url.startsWith('http://') || url.startsWith('https://')) {
        window.open(url, '_blank', 'noopener,noreferrer');
      } else {
        navigate(url);
      }
    }
  }, [activePopup]);

  return {
    popup: activePopup,
    dismiss: handleDismiss,
    snooze: handleSnooze,
    triggerAction: handleAction,
    resetDismissal: (id: string) => resetPopupDismissal(id),
  };
};
